import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { Alert } from '@restworld/ui-components';
import { formatGoogleOpeningHours } from '@restworld/utils-common';

import { useRestaurantOnboardingData } from './use-restaurant-onboarding-data-query';

const DayOpeningHour = ({ day, hours }: { day: string; hours: string }) => (
  <div className="self-stretch pt-2 border-t border-neutral-40 inline-flex justify-between items-start">
    <h5 className="justify-start text-neutral-200 text-sm font-normal capitalize">{day}</h5>
    <h5 className="justify-start text-neutral-700 text-sm font-medium">{hours}</h5>
  </div>
);

const RestaurantOnboardingOpeningHours = () => {
  const { t: tC } = useTranslation('common');
  const { t } = useTranslation('restaurant-onboarding');
  const data = useRestaurantOnboardingData();
  const publicHoursFormatted = useMemo(() => {
    const openingHours = data?.gmaps_info?.info?.opening_hours?.periods;
    if (openingHours) {
      const publicHours = formatGoogleOpeningHours(openingHours);
      return publicHours.map((hoursText) => {
        const [weekday, interval] = hoursText.split(': ');
        const hours = interval ? (interval === 'Closed' ? tC('closed') : interval) : ' - ';
        return {
          day: tC(weekday.toLowerCase()),
          hours: hours,
        };
      });
    }
    return [];
  }, [data?.gmaps_info?.info?.opening_hours?.periods, tC]);
  return (
    <div className="flex flex-col gap-3">
      <h5 className="justify-start text-neutral-700 text-base font-medium">{t('opening_hours')}</h5>
      <Alert variant="success" text={t('opening_hours_on_restworld_are_automatically_synchronized_with_google_maps')} />
      {!!publicHoursFormatted?.length && (
        <div className="self-stretch px-4 py-5 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-90 inline-flex flex-col justify-start items-start gap-4">
          {publicHoursFormatted.map(({ day, hours }) => (
            <DayOpeningHour key={day} day={day} hours={hours} />
          ))}
        </div>
      )}
    </div>
  );
};

export default RestaurantOnboardingOpeningHours;
